/**
 * Owner: Masa Bayu
 * File: natural-reader-highlights.ts
 * Date:Time: 2026-10-17 19:20
 * Description: QX Natural Reader 05 — highlight + nota per buku (persist
 *   localStorage, app-side). Simpan offsets watak dalam body halaman supaya
 *   semula lukis stabil walaupun teks berulang.
 */

export interface NaturalHighlight {
  readonly id: string;
  readonly pageIndex: number;
  readonly text: string;
  readonly startOffset: number;
  readonly endOffset: number;
  readonly note: string;
}

export const NATURAL_HIGHLIGHTS_STORAGE_KEY = 'qxread.natural-reader-highlights.v1';

type HighlightMap = Record<string, readonly NaturalHighlight[]>;

let idCounter = 0;

function nextId(): string {
  idCounter += 1;
  return `nrhl_${idCounter.toString(36)}_${Date.now().toString(36)}`;
}

function sanitizeHighlight(input: unknown): NaturalHighlight | null {
  if (typeof input !== 'object' || input === null) {
    return null;
  }
  const record = input as Record<string, unknown>;
  if (
    typeof record.pageIndex !== 'number' ||
    typeof record.text !== 'string' ||
    typeof record.startOffset !== 'number' ||
    typeof record.endOffset !== 'number'
  ) {
    return null;
  }
  const startOffset = Math.max(0, Math.round(record.startOffset));
  const endOffset = Math.max(startOffset, Math.round(record.endOffset));
  const text = record.text.slice(0, Math.max(0, endOffset - startOffset)) || record.text;
  return {
    id: typeof record.id === 'string' && record.id.length > 0 ? record.id : nextId(),
    pageIndex: Math.max(1, Math.round(record.pageIndex)),
    text,
    startOffset,
    endOffset,
    note: typeof record.note === 'string' ? record.note : '',
  };
}

function sanitize(input: unknown): HighlightMap {
  if (typeof input !== 'object' || input === null) {
    return {};
  }
  const map: HighlightMap = {};
  for (const [key, value] of Object.entries(input)) {
    if (!Array.isArray(value)) {
      continue;
    }
    map[key] = Object.freeze(
      value
        .map(sanitizeHighlight)
        .filter((entry): entry is NaturalHighlight => entry !== null),
    );
  }
  return map;
}

function readMap(): HighlightMap {
  try {
    const raw = window.localStorage.getItem(NATURAL_HIGHLIGHTS_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    return sanitize(JSON.parse(raw));
  } catch {
    return {};
  }
}

function writeMap(map: HighlightMap): void {
  try {
    window.localStorage.setItem(NATURAL_HIGHLIGHTS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage penuh/ditolak — kekal dalam memori sesi ini.
  }
}

export function listNaturalHighlights(publicationId: string): readonly NaturalHighlight[] {
  return readMap()[publicationId] ?? Object.freeze([] as NaturalHighlight[]);
}

export function listNaturalHighlightsForPage(
  publicationId: string,
  pageIndex: number,
): readonly NaturalHighlight[] {
  return listNaturalHighlights(publicationId).filter((entry) => entry.pageIndex === pageIndex);
}

export function addNaturalHighlight(
  publicationId: string,
  input: {
    readonly pageIndex: number;
    readonly text: string;
    readonly startOffset: number;
    readonly endOffset: number;
  },
): NaturalHighlight | null {
  const trimmed = publicationId.trim();
  const text = input.text.trim();
  if (
    trimmed.length === 0 ||
    text.length === 0 ||
    !Number.isFinite(input.startOffset) ||
    !Number.isFinite(input.endOffset) ||
    input.endOffset <= input.startOffset
  ) {
    return null;
  }
  const highlight: NaturalHighlight = Object.freeze({
    id: nextId(),
    pageIndex: Math.max(1, Math.round(input.pageIndex)),
    text,
    startOffset: Math.max(0, Math.round(input.startOffset)),
    endOffset: Math.round(input.endOffset),
    note: '',
  });
  const map = readMap();
  const current = map[trimmed] ?? [];
  map[trimmed] = Object.freeze([...current, highlight]);
  writeMap(map);
  return highlight;
}

export function updateNaturalHighlightNote(
  publicationId: string,
  highlightId: string,
  note: string,
): readonly NaturalHighlight[] {
  const map = readMap();
  const current = map[publicationId] ?? [];
  const frozen = Object.freeze(
    current.map((entry) =>
      entry.id === highlightId ? Object.freeze({ ...entry, note }) : entry,
    ),
  );
  map[publicationId] = frozen;
  writeMap(map);
  return frozen;
}

export function removeNaturalHighlight(
  publicationId: string,
  highlightId: string,
): readonly NaturalHighlight[] {
  const map = readMap();
  const current = map[publicationId] ?? [];
  const frozen = Object.freeze(current.filter((entry) => entry.id !== highlightId));
  map[publicationId] = frozen;
  writeMap(map);
  return frozen;
}

export function resetNaturalHighlights(): void {
  try {
    window.localStorage.removeItem(NATURAL_HIGHLIGHTS_STORAGE_KEY);
  } catch {
    // Abaikan.
  }
}
